import { useState, useEffect, DragEvent, ChangeEvent } from "react";
import { XLSXImporter } from "../common/XLSXImporter";
import CsvImporter from "../common/CsvImporter";
import styles from "./DataImporter.module.css";
import icon from "../../img/importer_icon.png";

import { useModal } from "../modal/Modal";
import ModalButtons from "../modal/ModalButtons";
import LoadingSpinner from "../common/LoadingSpinner";
import DataProvider from "../../data_provider/DataProvider";
import { useLoginStatus } from "../../hooks/useLoginStatus";

enum FileExtension {
    XLSX = "xlsx",
    XLS = "xls",
    CSV = "csv",
}

type FileHandlerMap = {
    // [key in FileExtension]: (file: File) => Promise<void>;
    [key in FileExtension]: (file: File) => Promise<string[][]>;
};

interface DataImporterProps {
    importData: (data: string[][]) => void;
}

/**
 * @author sukim91, smyun
 * @date 2022-08-26 17:26:59, 2025-05-15
 * @remark 엑셀 임포터 컴포넌트, 유저 권한 및 데이터 수 체크 로직 추가
 * @props None
 * @returns <section>
 */
export function DataImporter(props: DataImporterProps) {
    const [dragState, setDragState] = useState<boolean>(false);
    const [isFile, setIsFile] = useState<boolean>(true);
    const { ModalComponent, openModal, closeModal } = useModal();
    const [isLoading, setIsLoading] = useState(false);

    const [isMobileView, setIsMobileView] = useState(false);

    const isLogin = useLoginStatus();

    useEffect(() => {
        const checkViewport = () => {
            if (/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)) {
                setIsMobileView(true);
            } else {
                setIsMobileView(false);
            }
        };

        checkViewport();
        window.addEventListener("resize", checkViewport);
        return () => window.removeEventListener("resize", checkViewport);
    }, []);

    const xlsxImporter = new XLSXImporter();

    const fileHandlerMap: FileHandlerMap = {
        [FileExtension.XLS]: async (file) => {
            xlsxImporter.setFile(file);
            // const importedData = await xlsxImporter.import();
            // props.importData(importedData);
            return await xlsxImporter.import();
        },

        [FileExtension.XLSX]: async (file) => {
            xlsxImporter.setFile(file);
            // const importedData = await xlsxImporter.import();
            // props.importData(importedData);
            return await xlsxImporter.import();
        },

        [FileExtension.CSV]: async (file) => {
            // const csvData = await CsvImporter.csvParser(file);
            // props.importData(csvData);
            return await CsvImporter.csvParser(file);
        },
    };

    const containerClassName = !dragState
        ? styles.dnd_bx_container
        : isMobileView
        ? `${styles.dnd_bx_container_cant} ${styles.hover}`
        : `${styles.dnd_bx_container} ${styles.hover}`;

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragState(true);
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragState(false);
    };

    const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
        if (isMobileView) return;
        e.preventDefault();
        e.stopPropagation();

        if (e.dataTransfer) {
            const file = e.dataTransfer.files[0];
            const ext = file.name.split(".").pop()?.toLowerCase() as FileExtension;

            if (!(ext in fileHandlerMap)) {
                setIsFile(false);
                setDragState(false);
                return;
            }

            const showLimitModal = (message: string) => {
                setIsLoading(false); // 스피너만 끄기
                setDragState(false);
                openModal({
                    title: "안내",
                    children: message,
                    modalButtons: (
                        <ModalButtons
                            buttonCount={1}
                            buttons={[
                                {
                                    label: "확인",
                                    color: "Primary",
                                    onClick: () => closeModal(),
                                },
                            ]}
                        />
                    ),
                });
            };

            try {
                openModal({
                    title: "데이터를 처리중입니다...",
                    children: <LoadingSpinner />,
                });
                setIsLoading(true);

                // smyun | 유저 권한 및 데이터 수 체크 로직 추가
                // 사용자 정보

                const grade = isLogin ? (await DataProvider.GetUserInfo()).result.membership.gradeCode : 1;

                // 파일 처리
                const importedData = await fileHandlerMap[ext](file);

                const dataCount = importedData.length; // 지도 데이터 개수

                // 데이터에 1개 이하의 행이 존재하는 경우(필드이름만 있거나, 데이터만 있는 경우 형식 Validating)
                if (dataCount <= 1) {
                    showLimitModal("지도 생성용 데이터에는 필드이름과 내용이 필요합니다.");
                    return;
                }

                // 등급에 따라 데이터 수 제한
                if (grade == 1 && dataCount > 501) {
                    // 일반회원, 비회원 : 500개까지 가능
                    showLimitModal("일반회원/비회원은 500개의 데이터까지만 생성하실 수 있습니다.");
                    return;
                } else if (dataCount > 20001) {
                    // Pro, 관리자 : 20,000개까지 가능
                    showLimitModal("지도는 20,000개의 데이터까지만 생성하실 수 있습니다.");
                    return;
                }

                // 데이터 삽입
                props.importData(importedData);
            } catch (error) {
                openModal({
                    title: "안내",
                    children: error as string,
                    modalButtons: (
                        <ModalButtons
                            buttonCount={1}
                            buttons={[
                                {
                                    label: "확인",
                                    color: "Primary",
                                    onClick: () => {
                                        closeModal();
                                    },
                                },
                            ]}
                        />
                    ),
                });
            } finally {
                setIsLoading(false);
                // closeModal();
            }
        }
    };

    return (
        <>
            <ModalComponent />
            <label>
                <div className={containerClassName} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
                    <span className={`${styles.content} ${isMobileView ? styles.errorText : ""}`}>
                        {isMobileView ? (
                            <>파일 업로드는 PC에서만 지원됩니다.</>
                        ) : (
                            <>
                                XLXS 및 CSV 파일을 <br />
                                마우스로 드래그하여 추가해주세요.
                            </>
                        )}
                    </span>
                    <span className={`${isFile ? styles.hidden : styles.errorInfo}`}>지원하지 않는 파일 형식입니다.</span>
                    <img src={icon} className={styles.icon} />
                </div>
            </label>
            <input id={styles.fileUpload} type="file" className={styles.hidden} />
        </>
    );
}

export default DataImporter;
